import { useParams } from "react-router";
import AppPage from "layouts/AppPage";
import React, { useEffect, useMemo } from "react";
import { useNavbar } from "hooks/useNavbar";
import { useNewHireJourneyDetailQuery } from "features/NewHireJourney/hooks";
import { NotPeopleTeamPendingNewHireAlertBanner } from "components/AlertBanner/PendingNewHireAlertBanner";
import CaminoBreadcrumbs from "components/Breadcrumbs";
import { MANAGER_ROUTES } from "definitions/constants/routeConstants";
import {
  Box,
  Stack,
  useDisclosure,
} from "@chakra-ui/react";
import { groupTasksByUser } from "features/Resources/Task/helpers/filter";
import { useNewHireJourneyTasksQuery } from "features/Resources/Task/hooks";
import { NewHireJourneyId } from "models/newHire";
import { PrimaryButton } from "components/Button";
import SendRemindersQueueModal from "features/SendMessage/Components/SendRemindersQueueModal";
import { useReminderRows } from "features/SendMessage/hooks";
import { useCurrentUserQuery } from "features/User/hooks";
import ROLE from "models/role";
import { useAnalytics } from "services/analytics";
import ManagerNewHireHeader from "features/NewHireJourney/Components/NewHireHeader/ManagerNewHireHeader";
import TasksView from "features/Resources/Task/Components/TasksView";
import TaskActionModalByQueryParam from "features/Resources/Task/Components/Action/TaskActionModalByQueryParam";

function ManagerNewHirePage() {
  const { id } = useParams<{ id: string }>();
  const { setPageTitle } = useNavbar();
  const { trackButtonClick, trackPageView } = useAnalytics();

  const { data, isLoading, isFetched } = useNewHireJourneyDetailQuery(id || "");
  const { data: currentUser } = useCurrentUserQuery();

  useEffect(() => {
    if (data?.newHire?.fullName && !isLoading) {
      setPageTitle(`New Hire ${data?.newHire?.fullName ? data.newHire.fullName : ""}`);
    }
    return () => {
      setPageTitle(null);
    };
  }, [data?.newHire?.fullName, isLoading, setPageTitle]);

  useEffect(() => {
    trackPageView("manager.new_hire_detail", { source: "manager" });
  }, [trackPageView]);

  const { data: tasks } = useNewHireJourneyTasksQuery(id as NewHireJourneyId, {
    enabled: !!id,
  });

  const tasksByUser = useMemo(() => groupTasksByUser(tasks, {
    newHire: data?.newHire,
    manager: data?.manager,
    my: currentUser,
  }), [currentUser, data?.manager, data?.newHire, tasks]);

  const teamTasks = useMemo(() => {
    const assignedUserIds = [data?.newHire?.id, data?.manager?.id].filter(Boolean);
    return (tasks ?? []).filter((task) => !assignedUserIds.includes(task.assigneeId));
  }, [data?.manager, data?.newHire, tasks]);

  // Send Reminders modal
  const reminderDisclosure = useDisclosure();
  const { rows, youRow } = useReminderRows(data, { currentUserId: currentUser?.id });

  const canEditDetails = currentUser?.permission?.some((r) =>
    ([ROLE.PEOPLE_TEAM, ROLE.COMPANY_ADMIN] as string[]).includes(r),
  );
  const hasStakeholdersToRemind = rows.length > 0;

  return (
    <AppPage>
      <Stack
        maxWidth="1080px"
        w="100%"
        direction={{ base: "column", sm: "row" }}
        align="center"
        justifyContent={{ base: "flex-start", sm: "space-between" }}
        pb={{ base: "2", md: "0" }}
      >
        <CaminoBreadcrumbs
          breadcrumbInfos={[
            { name: "Dashboard", toUrl: MANAGER_ROUTES.dashboard },
            {
              name: isFetched && data?.newHire.fullName
                ? data.newHire.fullName
                : "loading...",
            },
          ]}
        />
        {hasStakeholdersToRemind && (
          <PrimaryButton
            h="32px"
            onClick={() => {
              reminderDisclosure.onOpen();
              trackButtonClick("Send Reminders", {
                source: "manager.new_hire_detail",
                source_type: "header",
              });
            }}
          >
            Send Reminders
          </PrimaryButton>
        )}
      </Stack>

      {data?.isPending && (
        <NotPeopleTeamPendingNewHireAlertBanner newHireId={id as string} />
      )}

      <Box w="100%" gap="2" my="2">
        <ManagerNewHireHeader newHireJourney={data} tasks={tasks} canEditDetails={canEditDetails} />
      </Box>

      <Box mt="4">
        <TasksView
          newHireJourney={data}
          allTasks={tasks}
          myTasks={tasksByUser.my}
          managerTasks={tasksByUser.manager}
          newHireTasks={tasksByUser.newHire}
          teamTasks={teamTasks}
        />
      </Box>

      <TaskActionModalByQueryParam />

      <SendRemindersQueueModal
        isOpen={reminderDisclosure.isOpen}
        onClose={reminderDisclosure.onClose}
        rows={rows}
        youRow={youRow}
        newHireJourneyId={id as string}
        newHire={data?.newHire ? {
          fullName: data.newHire.fullName,
          avatarUrl: data.newHire.avatarUrl,
          title: data.newHire.title,
        } : undefined}
      />
    </AppPage>
  );
}

export default React.memo(ManagerNewHirePage);
